import { CartLinesUpdateEvent, ProductSelectEvent } from '@shopify/events';

class EmilyBundleBuilder extends HTMLElement {
  connectedCallback() {
    this.section = this.closest('.shopify-section');
    const dataEl = this.querySelector('[data-emily-bundle-data]');
    if (!dataEl) return;
    try { this.data = JSON.parse(dataEl.textContent); } catch { return; }
    this.colors = this.data.colors || [];
    this.sizes = this.data.sizes || [];
    this.variants = this.data.variants || [];
    this.colorHex = {
      'black': '#1d1d1f', 'white': '#f7f5f0', 'ivory': '#efe9dc', 'cream': '#efe9dc', 'beige': '#d9cbb8',
      'nude': '#d9bfa8', 'blush': '#e8c4bb', 'pink': '#e8a9b8', 'rose': '#d98e94', 'red': '#b3413a',
      'burgundy': '#6e2c34', 'brown': '#7a5c46', 'mocha': '#8a6f5a', 'tan': '#c8a882', 'camel': '#b99268',
      'taupe': '#a99a8b', 'gray': '#9aa1a8', 'grey': '#9aa1a8', 'charcoal': '#4a4d52', 'navy': '#2c3a52',
      'blue': '#4a6b9e', 'sky': '#a7c3dd', 'sage': '#a9bba4', 'green': '#6f8a6d', 'olive': '#7c7d5a',
      'purple': '#8a7ba6', 'lavender': '#c3b8dd', 'yellow': '#e5c558', 'orange': '#dd8a55'
    };
    this.slotsEl = this.querySelector('[data-emily-slots]');
    this.addBtn = this.querySelector('[data-emily-add]');
    this.addTotal = this.querySelector('[data-emily-add-total]');
    this.errorEl = this.querySelector('[data-emily-error]');
    this.state = { tier: 2, slots: [] };

    // Default slot choices: first available color + first available size.
    const firstAv = this.variants.find((v) => v.av) || this.variants[0];
    this.defaultColor = firstAv ? firstAv.c : this.colors[0];
    this.defaultSize = firstAv ? firstAv.s : this.sizes[0];

    this.querySelectorAll('input[type="radio"]').forEach((radio) => {
      if (radio.checked) this.state.tier = Number(radio.value);
      radio.addEventListener('change', () => {
        this.state.tier = Number(radio.value);
        this.renderSlots();
      });
    });
    this.addBtn.addEventListener('click', () => this.addToCart());
    // The builder owns quantity and add-to-cart; hide the native equivalents.
    const stepper = this.section?.querySelector('quantity-selector-component');
    if (stepper) stepper.style.display = 'none';
    this.section?.querySelectorAll('product-form-component .add-to-cart-button, product-form-component button[type="submit"]').forEach((b) => {
      if (!b.closest('sticky-add-to-cart')) b.style.display = 'none';
    });
    this.renderSlots();
    this.updateThumbs();
    this.hidden = false;
  }

  variantFor(color, size) {
    return this.variants.find((v) => v.c === color && v.s === size);
  }

  tierIncluded() {
    const row = this.querySelector(`.emily-offer__row[data-paid="${this.state.tier}"]`);
    return Number(row?.dataset.included || this.state.tier);
  }

  renderSlots() {
    const count = this.tierIncluded();
    this.state.slots = Array.from({ length: count }, (_, i) => this.state.slots[i] || { c: this.defaultColor, s: this.defaultSize });
    this.state.slots.length = count;

    this.slotsEl.innerHTML = '';
    this.slotsEl.hidden = false;
    this.state.slots.forEach((slot, i) => {
      const box = document.createElement('div');
      box.className = 'emily-bundle__slot';
      box.innerHTML = `
        <p class="emily-bundle__slot-title">${count === 1 ? 'Your tank' : 'Tank ' + (i + 1)}</p>
        <div class="emily-bundle__choices" data-slot="${i}">
          <div class="emily-bundle__swatches" role="radiogroup" aria-label="Tank ${i + 1} color"></div>
          <div class="emily-bundle__sizes" role="radiogroup" aria-label="Tank ${i + 1} size"></div>
        </div>`;
      this.slotsEl.appendChild(box);
      const swatchBox = box.querySelector('.emily-bundle__swatches');
      const sizeBox = box.querySelector('.emily-bundle__sizes');
      this.colors.forEach((color) => {
        const available = this.variants.some((v) => v.c === color && v.av);
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'emily-bundle__swatch' + (slot.c === color ? ' is-selected' : '') + (available ? '' : ' is-unavailable');
        b.title = color + (available ? '' : ', sold out');
        b.setAttribute('aria-label', color + (available ? '' : ', sold out'));
        b.dataset.color = color;
        const hex = this.colorHex[color.toLowerCase().trim()];
        if (hex) {
          b.style.background = hex;
          b.classList.add('has-color');
        } else {
          b.textContent = color.trim().charAt(0).toUpperCase();
          b.classList.add('has-letter');
        }
        b.disabled = !available;
        b.addEventListener('click', () => this.pick(i, 'c', color));
        swatchBox.appendChild(b);
      });
      this.sizes.forEach((size) => {
        const available = this.variants.some((v) => v.s === size && v.av && v.c === slot.c);
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'emily-bundle__size' + (slot.s === size ? ' is-selected' : '') + (available ? '' : ' is-unavailable');
        b.textContent = size;
        b.dataset.size = size;
        b.disabled = !available;
        b.addEventListener('click', () => this.pick(i, 's', size));
        sizeBox.appendChild(b);
      });
    });
    this.updatePrices();
    this.updateThumbs();
  }

  // Tier card thumbnails follow the first slot's selected color.
  updateThumbs() {
    const slot = this.state.slots[0];
    if (!slot) return;
    const variant = this.variantFor(slot.c, slot.s) || this.variants.find((v) => v.c === slot.c);
    if (!variant?.img) return;
    const scaled = variant.img.replace('/240w', '/120w');
    this.querySelectorAll('.emily-offer__thumb img').forEach((img) => {
      if (img.getAttribute('src') !== scaled) img.setAttribute('src', scaled);
      img.removeAttribute('srcset');
    });
  }

  // Mirror the native variant picker: tell the theme (gallery, price, sticky bar)
  // that the first slot's variant changed, exactly like the picker does.
  syncNativeSelection() {
    const slot = this.state.slots[0];
    if (!slot) return;
    const variant = this.variantFor(slot.c, slot.s);
    if (!variant) return;
    const idInput = this.section?.querySelector('form input[name="id"], input[name="id"]');
    if (idInput) {
      idInput.value = String(variant.id);
      idInput.dispatchEvent(new Event('change', { bubbles: true }));
    }
    const sectionEl = this.closest('.shopify-section');
    if (!sectionEl) return;
    const sectionId = sectionEl.id.replace('shopify-section-', '');
    const deferred = ProductSelectEvent.createPromise();
    this.dispatchEvent(new ProductSelectEvent({
      product: { id: '', title: '', handle: window.location.pathname.split('/').pop() || '' },
      selectedOptions: [slot.c, slot.s],
      detail: { variantId: variant.id },
      promise: deferred.promise,
    }));
    this.variantFetch?.abort();
    this.variantFetch = new AbortController();
    // Variant params only apply on the product's own URL; on the homepage the
    // payload carries the product URL so the section still renders the variant.
    const baseUrl = this.data.product_url || window.location.pathname;
    const url = `${baseUrl}?variant=${variant.id}&section_id=${sectionId}`;
    fetch(url, { signal: this.variantFetch.signal })
      .then((r) => r.text())
      .then((htmlText) => {
        const doc = new DOMParser().parseFromString(htmlText, 'text/html');
        // product-form reads detail.resource.id into its variant input, so pass a
        // minimal variant resource alongside the re-rendered section HTML.
        const resource = {
          id: `gid://shopify/ProductVariant/${variant.id}`,
          title: `${slot.s} / ${slot.c}`,
          available: true,
          price: { amount: (variant.p / 100).toFixed(2), currencyCode: 'USD' },
        };
        deferred.resolve({ variant: null, detail: { html: doc, productId: '', sourceId: '', resource } });
        if (window.location.pathname === baseUrl) {
          window.history.replaceState({}, '', `${baseUrl}?variant=${variant.id}`);
        }
      })
      .catch((err) => {
        if (err?.name !== 'AbortError') deferred.reject(err);
      });
  }

  pick(slotIndex, key, value) {
    this.state.slots[slotIndex][key] = value;
    const slot = this.state.slots[slotIndex];
    if (!this.variantFor(slot.c, slot.s)?.av) {
      const fallback = this.variants.find((v) => v.c === slot.c && v.av);
      if (fallback) this.state.slots[slotIndex].s = fallback.s;
    }
    this.renderSlots();
    if (slotIndex === 0) this.syncNativeSelection();
  }

  updatePrices() {
    const unit = this.variants.find((v) => v.av)?.p || this.variants[0]?.p || 0;
    const paid = this.state.tier;
    const included = this.tierIncluded();
    const fmt = (cents) => `$${(cents / 100).toFixed(2)}`;
    const row = this.querySelector(`.emily-offer__row[data-paid="${paid}"]`);
    if (row) {
      row.querySelector('[data-emily-total]').textContent = fmt(unit * paid);
      const compare = row.querySelector('[data-emily-compare]');
      const unitEl = row.querySelector('[data-emily-unit]');
      if (included > paid) {
        if (compare) compare.textContent = fmt(unit * included);
        if (unitEl) unitEl.textContent = `${fmt((unit * paid) / included)}/tank`;
      } else if (unitEl) {
        unitEl.textContent = `${fmt(unit)}/tank`;
      }
    }
    this.addTotal.textContent = fmt(unit * paid);
  }

  resolveCartItems() {
    const groups = new Map();
    for (const slot of this.state.slots) {
      const variant = this.variantFor(slot.c, slot.s);
      if (!variant || !variant.av) return null;
      groups.set(variant.id, (groups.get(variant.id) || 0) + 1);
    }
    return [...groups.entries()].map(([id, quantity]) => ({ id, quantity }));
  }

  async addToCart() {
    if (this.addBtn.disabled) return;
    const items = this.resolveCartItems();
    if (!items) {
      this.errorEl.textContent = 'One of your tanks is sold out in that size, pick another combination.';
      this.errorEl.hidden = false;
      return;
    }
    this.errorEl.hidden = true;
    this.addBtn.disabled = true;
    this.addBtn.classList.add('is-loading');
    // Horizon's cart drawer + icon refresh and auto-open off this event.
    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: items.map(({ id, quantity }) => ({ merchandiseId: String(id), quantity })),
      promise: deferred.promise,
    }));
    try {
      const cartItemsComponents = [...document.querySelectorAll('cart-items-component')]
        .map((c) => c.dataset.sectionId)
        .filter(Boolean);
      const body = { items };
      if (cartItemsComponents.length) body.sections = cartItemsComponents.join(',');
      const res = await fetch(window.Theme?.routes?.cart_add_url || '/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error(`Add failed (${res.status})`);
      const addResponse = await res.json();
      // Resolve with the canonical cart payload: add.js responses lack fields
      // (e.g. currency) that Shopify's event library needs to build totals.
      const ajaxCart = await (await fetch('/cart.js', { headers: { Accept: 'application/json' } })).json();
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
        detail: {
          items: ajaxCart.items,
          source: 'emily-bundle-builder',
          itemCount: ajaxCart.item_count,
          sections: addResponse.sections,
          didError: false,
        },
      });
    } catch (err) {
      deferred.reject(err);
      this.errorEl.textContent = 'Sorry, something went wrong adding your bundle. Please try again.';
      this.errorEl.hidden = false;
    } finally {
      this.addBtn.disabled = false;
      this.addBtn.classList.remove('is-loading');
    }
  }
}
if (!customElements.get('emily-bundle-builder')) customElements.define('emily-bundle-builder', EmilyBundleBuilder);
